/**
 * Registration prompt component shown when anonymous trial limits are reached.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, Check, Clock, X } from 'lucide-react';
import { getRegistrationPrompt, RegistrationPrompt } from '../api/anonymous';
import { useTransition } from '../hooks/useTransition';

interface RegistrationPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  resetTime?: Date;
  promptOverride?: RegistrationPrompt | null;
}

export function RegistrationPromptModal({
  isOpen,
  onClose,
  resetTime,
  promptOverride,
}: RegistrationPromptModalProps) {
  const [prompt, setPrompt] = useState<RegistrationPrompt | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const { shouldMount, isVisible } = useTransition(isOpen && !!prompt, 200);

  useEffect(() => {
    if (isOpen) {
      if (promptOverride) {
        setPrompt(promptOverride);
        return;
      }

      getRegistrationPrompt()
        .then(setPrompt)
        .catch(() => {
          // Use default prompt if API fails
          setPrompt({
            title: 'Unlock Full Access',
            message: "You've used your free trial for today. Register now to continue transcribing!",
            benefits: [
              'More transcriptions per day',
              'Keep your transcriptions forever',
              'Access from any device',
              'Share links with others',
              'Priority processing',
            ],
          });
        });
    }
  }, [isOpen, promptOverride]);

  useEffect(() => {
    if (!resetTime) return;

    const updateTimeRemaining = () => {
      const now = new Date();
      const diff = resetTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Available now');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m until reset`);
      } else {
        setTimeRemaining(`${minutes}m until reset`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [resetTime]);

  if (!shouldMount) return null;
  if (!prompt) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm ${isVisible ? 'modal-backdrop-in' : 'modal-backdrop-out'}`}>
      <div className={`relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 ${isVisible ? 'modal-content-in' : 'modal-content-out'}`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <UserPlus className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        {/* Title and message */}
        <h2 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">
          {prompt.title}
        </h2>
        <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
          {prompt.message}
        </p>

        {/* Reset time */}
        {timeRemaining && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
            <Clock className="w-4 h-4" />
            <span>{timeRemaining}</span>
          </div>
        )}

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          {prompt.benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-5 h-5 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-gray-700 dark:text-gray-200">{benefit}</span>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <Link
            to="/register"
            className="block w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-center transition-colors"
          >
            Create Free Account
          </Link>
          <Link
            to="/login"
            className="block w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg text-center transition-colors"
          >
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

interface TrialLimitBannerProps {
  remainingFiles: number;
  dailyLimit: number;
  resetTime?: Date;
  onDismiss?: () => void;
  showWhenFull?: boolean;
}

export function TrialLimitBanner({
  remainingFiles,
  dailyLimit,
  onDismiss,
  showWhenFull = false,
}: TrialLimitBannerProps) {
  const isAtLimit = remainingFiles === 0;

  if (!showWhenFull && remainingFiles === dailyLimit) {
    return null; // Don't show banner if no files used yet
  }

  return (
    <div
      className={`rounded-lg p-4 ${
        isAtLimit
          ? 'bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
          : 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3
            className={`font-medium ${
              isAtLimit
                ? 'text-orange-800 dark:text-orange-200'
                : 'text-blue-800 dark:text-blue-200'
            }`}
          >
            {isAtLimit ? 'Trial Limit Reached' : 'Free Trial'}
          </h3>
          <p
            className={`mt-1 text-sm ${
              isAtLimit
                ? 'text-orange-700 dark:text-orange-300'
                : 'text-blue-700 dark:text-blue-300'
            }`}
          >
            {isAtLimit
              ? "You've used your free transcription for today."
              : `${remainingFiles} of ${dailyLimit} free transcriptions remaining.`}
          </p>
          {isAtLimit && (
            <Link
              to="/register"
              className="inline-block mt-2 text-sm font-medium text-orange-800 dark:text-orange-200 underline hover:no-underline"
            >
              Create an account to continue
            </Link>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`ml-4 ${
              isAtLimit
                ? 'text-orange-400 hover:text-orange-600'
                : 'text-blue-400 hover:text-blue-600'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default RegistrationPromptModal;
